/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {usePDPSearchParams} from '@salesforce/retail-react-app/app/hooks/use-pdp-search-params'

/**
 * Gets the first available color for a card type from variationGroups (sorted numerically)
 * @param {Object} product - The product object
 * @param {string} cardType - The card type value (e.g., 'EGC', 'CGC')
 * @returns {string|null} The first available color for this card type
 */
const getFirstColorForCardType = (product, cardType) => {
    if (!product?.variationGroups) return null
    const colors = product.variationGroups
        .filter((vg) => vg.variationValues?.cardType === cardType)
        .sort((a, b) => {
            const colorA = a.variationValues?.color || ''
            const colorB = b.variationValues?.color || ''
            return colorA.localeCompare(colorB, undefined, {numeric: true})
        })
        .map((vg) => vg.variationValues?.color)
    return colors[0] || null
}

/**
 * Gets default cardType for a gift card master product
 * @param {Object} product - The product object
 * @returns {string|null} The default card type ('EGC' or 'CGC')
 */
const getDefaultCardTypeForGiftCard = (product) => {
    if (!product?.variationGroups) return null

    // Get all available card types from variationGroups
    const availableCardTypes = new Set()
    product.variationGroups.forEach((vg) => {
        if (vg.variationValues?.cardType) {
            availableCardTypes.add(vg.variationValues.cardType)
        }
    })

    // If c_isEGiftCard is true and EGC exists, default to EGC
    if (product.c_isEGiftCard && availableCardTypes.has('EGC')) {
        return 'EGC'
    }
    // If c_isGiftCard is true and CGC exists, default to CGC
    if (product.c_isGiftCard && availableCardTypes.has('CGC')) {
        return 'CGC'
    }
    // Fallback to first available card type
    return Array.from(availableCardTypes)[0] || null
}

/*
 * This hook will return only the params that are also product attributes for the
 * passed in product object.
 */
export const useVariationParams = (
    product = {},
    isProductPartOfSet = false,
    isProductPartOfBundle = false,
    useParams = true
) => {
    const {variationAttributes = [], variationValues = {}} = product

    const [allParams, productParams] = usePDPSearchParams(product.id)
    const params = isProductPartOfSet || isProductPartOfBundle ? productParams : allParams

    // Check if this is a gift card master product (no cardType/color in variationValues)
    const isGiftCardMaster =
        (product?.c_isGiftCard || product?.c_isEGiftCard) &&
        product?.type?.master &&
        !variationValues?.cardType

    if (!useParams) {
        // Return only the variationValues from product (no URL params)
        const variationParams = variationAttributes
            .map(({id}) => id)
            .reduce((acc, key) => {
                const value = variationValues?.[key]
                return value ? {...acc, [key]: value} : acc
            }, {})

        return variationParams
    }

    // Using all the variation attribute id from the array generated below, get
    // the value if there is one from the location search params and add it to the
    // accumulator.
    const variationParams = variationAttributes
        .map(({id}) => id)
        .reduce((acc, key) => {
            let value = params.get(`${key}`) || variationValues?.[key]

            // For gift card master products, set default cardType and color
            // when not provided in URL or variationValues
            if (isGiftCardMaster && !value) {
                if (key === 'cardType') {
                    value = getDefaultCardTypeForGiftCard(product)
                } else if (key === 'color') {
                    // Get default color based on the cardType (from URL, acc, or default)
                    const cardType =
                        params.get('cardType') ||
                        acc.cardType ||
                        getDefaultCardTypeForGiftCard(product)
                    if (cardType) {
                        value = getFirstColorForCardType(product, cardType)
                    }
                }
            }

            return value ? {...acc, [key]: value} : acc
        }, {})

    return variationParams
}
